import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { clearSelectedAccount, loadSelectedAccount } from '../../actions/accountActions';
import * as accountApi from '../../api/accountApi';
import * as userSettingsApi from '../../api/userSettingsApi';
import { applyThemeSettings, clearThemeSettings } from '../../services/portalBranding';
import { Loadable } from '../../models';
import { AccountDetail, AccountInfo } from '../../models/account';
import { QueryResult } from '../../models/queryResult';
import { AppState } from '../../models/state/appState';
import SearchError from '../search/SearchError';
import SearchInfo from '../search/SearchInfo';
import SearchWarning from '../search/SearchWarning';
import SelectorMenuLoading from '../ui/SelectorMenuLoading';

interface Props {
  companyId: number;
  accountId?: number;
  onSelectAccount?: (account: AccountInfo) => void;
  onAccountMenuRefresh?: (accounts: AccountInfo[]) => void;
  excludedAccounts?: number[];
  displayAsFormControl?: boolean;
  onNavClick?: () => void;
}

type SearchStatus = 'Empty' | 'NoResults' | 'Results' | 'Error' | 'Loading';

export default function AccountMenu({
  companyId,
  accountId,
  onSelectAccount: onSelectAccountProp,
  onAccountMenuRefresh,
  excludedAccounts,
  displayAsFormControl,
}: Props) {
  const dispatch: Dispatch<any> = useDispatch();
  const currentAccount = useSelector<AppState, Loadable<AccountDetail>>((state) => state.filter.account);
  const userId = useSelector<AppState, string | undefined>((state) => state.userSettings.userId);

  const [searchText, setSearchText] = useState('');
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [count, setCount] = useState<number>();
  const [totalCount, setTotalCount] = useState<number>();
  const [status, setStatus] = useState<SearchStatus>('Empty');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLLIElement>(null);

  // The class kept the loaded accounts' companyId in state purely to spot this change in
  // getDerivedStateFromProps; the results belong to one company, so they are dropped on a switch.
  useEffect(() => {
    setAccounts([]);
  }, [companyId]);

  useEffect(() => {
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  const onSelectAccount = (e: any, selectedAccountId: number | null) => {
    setIsMenuOpen(false);
    if (onSelectAccountProp) {
      e.preventDefault();
      e.stopPropagation();
      const selectedAccount = accounts.find((f) => f.id === selectedAccountId);
      if (selectedAccount) {
        onSelectAccountProp(selectedAccount);
      }
    } else if (selectedAccountId && selectedAccountId !== accountId) {
      dispatch(loadSelectedAccount({ companyId, accountId: selectedAccountId }));
    }

    if (!userId) {
      return;
    }

    userSettingsApi
      .getTheme(userId, companyId, selectedAccountId)
      .then((reply: Record<string, string> | null) => {
        if (reply !== null) {
          applyThemeSettings(reply);
          return;
        }

        clearThemeSettings();
      })
      .catch(() => {
        clearThemeSettings();
      });
  };

  function search() {
    if (!companyId) {
      return;
    }

    setStatus('Loading');

    accountApi
      .get(companyId, {
        take: 75,
        searchTerm: searchText,
        wrapWith: {
          totalCount: true,
        },
        excludedAccounts,
      })
      .then((result: QueryResult<AccountInfo>) => {
        setAccounts(result.result);
        setStatus(result.result.length ? 'Results' : 'NoResults');
        setCount(result.count);
        setTotalCount(result.totalCount);

        if (onAccountMenuRefresh) {
          onAccountMenuRefresh(result.result);
        }
      })
      .catch(() => setStatus('Error'));
  }

  function getCurrentAccount(): Loadable<AccountDetail> {
    if (accountId === currentAccount.id) {
      return currentAccount;
    }

    if (accountId) {
      const selectedAccount = accounts.find((f) => f.id === accountId);
      return {
        id: selectedAccount ? selectedAccount.id : 0,
        name: selectedAccount ? selectedAccount.name : undefined,
        isAtlasAccount: false,
        isLoading: false,
      };
    }

    return currentAccount;
  }

  function renderTitle() {
    const { isLoading, error, id, name } = getCurrentAccount();
    if (isLoading) {
      return 'Loading...';
    }

    if (error || id === 0) {
      return 'Account';
    }

    return `Account ${id}` + (name ? ` (${name})` : '');
  }

  function renderMenuItems(): React.JSX.Element {
    const items = accounts.map((account) => (
      <li className={account.id === currentAccount.id ? 'selected' : ''} key={account.id}>
        <Link
          className="menuItemLink"
          to={`/company/${companyId}/account/${account.id}/sim`}
          onClick={(e) => onSelectAccount(e, account.id)}
        >
          {`${account.id}` + (account.name ? ` (${account.name})` : '')}
        </Link>
      </li>
    ));

    if (items.length === 0) {
      return <SearchInfo content="No results." />;
    }

    return (
      <div className="search-results noindent">
        <ul>{items}</ul>
        {totalCount === count ? (
          <SearchInfo
            content={
              <span>
                Search found <strong>{totalCount}</strong>
                &nbsp;{totalCount === 1 ? 'result' : 'results'}.
              </span>
            }
          />
        ) : (
          <SearchWarning count={count!} totalCount={totalCount!} />
        )}
      </div>
    );
  }

  function renderResultsSection(): React.JSX.Element | null {
    switch (status) {
      case 'Loading':
        return <SelectorMenuLoading />;
      case 'Results':
        return renderMenuItems();
      case 'NoResults':
        return <SearchInfo content="No results." />;
      case 'Empty':
        return null;
      case 'Error':
        return <SearchError content="An error occurrred while attempting to search for the account." />;
      default:
        return <div>Unknown</div>;
    }
  }

  return (
    <div className="account-select">
      <ul className={(!displayAsFormControl ? 'nav navbar-nav' : '') + ' selector-menu'}>
        <li
          className={
            'dropdown selector-title ' +
            `${displayAsFormControl ? 'form-control' : ''}` +
            `${isMenuOpen ? ' open show' : ''}`
          }
          ref={menuRef}
        >
          <a
            className="dropdown-toggle"
            id="account-selector-menu"
            href="#"
            aria-haspopup="true"
            aria-expanded={isMenuOpen}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              // Deliberately not stopping propagation: the click has to reach document so the
              // company menu's outside-click listener closes it, otherwise both dropdowns render on
              // top of each other. This menu's own listener ignores clicks inside menuRef.
              setIsMenuOpen((prev) => !prev);
            }}
          >
            <span id="accountTitle">{renderTitle()}</span>
          </a>
          {accountId && (
            <Link
              className="clear-account-link"
              to={`/company/${companyId}/sim`}
              onClick={(e: any) => {
                dispatch(clearSelectedAccount());
                onSelectAccount(e, null);
              }}
              aria-label="Clear account"
              title="Clear account"
            >
              <span className="clear-account-button">
                <i className="fa-solid fa-circle-xmark" aria-hidden="true" />
              </span>
            </Link>
          )}

          <div
            className={`dropdown-menu csm-dropdown-search${isMenuOpen ? ' show' : ''}`}
            aria-labelledby="account-selector-menu"
          >
            <div className="input-group search-section">
              <input
                type="text"
                className="form-control search-box"
                name="accountSearch"
                onChange={(e) => setSearchText(e.target.value)}
                onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.keyCode === 13) {
                    search();
                  }
                }}
                placeholder="Search..."
              />
              <Button
                variant="primary"
                aria-label="Submit search"
                onClick={(e: React.SyntheticEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  search();
                }}
              >
                <i className="fa-solid fa-magnifying-glass"></i>
              </Button>
            </div>
            {renderResultsSection()}
          </div>
        </li>
      </ul>
    </div>
  );
}
